import { cn } from "@/lib/cn";

export interface TabItem {
  label: string;
  value: string;
}

interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  "aria-label"?: string;
}

/**
 * Horizontal tab bar. Visual style matches Explore's segmented tab treatment:
 * a muted rounded container with an active pill (white + shadow-sm + ring).
 */
export function Tabs({ items, value, onChange, className, "aria-label": ariaLabel }: TabsProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "flex flex-wrap gap-1 rounded-xl bg-[var(--color-surface-muted)] p-1 ring-1 ring-[var(--color-border)]",
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(item.value)}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-white text-[var(--color-text)] shadow-sm ring-1 ring-[var(--color-border)]"
                : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
